import { FC } from 'react';

import { AvatarSize, Box, BoxProps, GlobeOutline, IconProps, OfficeBuildingOutline, rem, UserGroupOutline } from '@bonusly/design-system';

// The three "system" group dimensions that share the color-coded chip treatment.
// `everyone` / `csv_upload` recipients intentionally fall back to their avatar art
// since they have no equivalent in the Nominations design.
export type GroupAvatarType = 'department' | 'location' | 'team';

export const SYSTEM_GROUP_AVATAR_TYPES: GroupAvatarType[] = ['department', 'location', 'team'];

// Narrows a recipient/group dimension string to one of the color-coded system
// group types. Callers use this to decide between a `GroupAvatar` and the legacy
// avatar art for `everyone` / `csv_upload`.
export const isGroupAvatarType = (dimension: string | null | undefined): dimension is GroupAvatarType =>
	!!dimension && SYSTEM_GROUP_AVATAR_TYPES.includes(dimension as GroupAvatarType);

export const GROUP_TYPE_ICON: Record<GroupAvatarType, FC<IconProps>> = {
	department: OfficeBuildingOutline,
	location: GlobeOutline,
	team: UserGroupOutline,
};

export const GROUP_TYPE_BG_COLOR: Record<GroupAvatarType, string> = {
	department: 'purpleAlpha.250',
	location: 'greenAlpha.200',
	team: 'blueAlpha.200',
};

export const GROUP_TYPE_ICON_COLOR: Record<GroupAvatarType, string> = {
	department: 'purple.400',
	location: 'green.400',
	team: 'blue.400',
};

// Box dimensions mirror the design-system Avatar sizes (`AvatarSizeMap`) so a
// `GroupAvatar` can drop into a layout that previously rendered an `Avatar`
// without disturbing the surrounding spacing. Icon sizes are ~50% of the box.
const SIZE_TOKENS: Record<AvatarSize, { box: number; icon: IconProps['size'] }> = {
	sm: { box: 32, icon: 'xs' },
	md: { box: 40, icon: 'sm' },
	lg: { box: 48, icon: 'md' },
	xl: { box: 56, icon: 'lg' },
	'2xl': { box: 72, icon: 'xl' },
};

type GroupAvatarProps = Omit<BoxProps, 'size'> & {
	groupType: GroupAvatarType;
	size?: AvatarSize;
};

// Circular icon-as-avatar for group recipients (department / location / team).
// This is the shared standard introduced by Nominations and reused across give
// box, feeds, and external recognition so the same group concept reads
// consistently everywhere.
export const GroupAvatar: FC<GroupAvatarProps> = ({ groupType, size = 'sm', ...rest }) => {
	const Icon = GROUP_TYPE_ICON[groupType];
	const { box, icon } = SIZE_TOKENS[size];

	return (
		<Box
			w={rem(box)}
			h={rem(box)}
			minW={rem(box)}
			bgColor={GROUP_TYPE_BG_COLOR[groupType]}
			borderRadius="full"
			display="flex"
			alignItems="center"
			justifyContent="center"
			color={GROUP_TYPE_ICON_COLOR[groupType]}
			{...rest}
		>
			<Icon size={icon} />
		</Box>
	);
};
